/**
 * MINI GERENCIAMENTO — PAINEL DA EQUIPE.
 *
 * A porta de entrada: "como está a minha equipe agora?" — alunos, turmas,
 * chamada de hoje e mensalidades do mês. Só leitura (as ações ficam nas abas).
 */
import { useCallback, useEffect, useState } from 'react';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import {
  AlertTriangle, CalendarCheck, ClipboardList, DollarSign, FileSpreadsheet, FileText,
  Loader2, Users, Wallet, ShieldPlus,
} from 'lucide-react';
import { minhaEquipeApi, equipeSolicitacaoApi, federationApi } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { competenciaTexto, formatarValor } from '../../lib/minhaEquipe';

function Cartao({ icone, rotulo, valor, detalhe, cor = 'text-white' }) {
  return (
    <div className="rounded-lg border border-[#27272A] bg-[#141414] p-4">
      <div className="flex items-center gap-2 micro-rotulo">
        {icone} {rotulo}
      </div>
      <p className={`text-2xl font-bold mt-1 ${cor}`}>{valor}</p>
      {detalhe && <p className="text-micro text-[#71717A] mt-0.5">{detalhe}</p>}
    </div>
  );
}

export default function PainelEquipe({ onIrPara }) {
  const [dados, setDados] = useState(null);
  const [carregando, setCarregando] = useState(true);
  /* RELATORIO DE ALUNOS — equipe escolhida no seletor (1 team = pre-selecionada) */
  const [equipeRelatorio, setEquipeRelatorio] = useState('');
  /* PEDIDO DE CADASTRO DE EQUIPE — perfil `equipe` sem equipe */
  const { user } = useAuth();
  const [solicitacao, setSolicitacao] = useState(null);
  const [formEquipe, setFormEquipe] = useState({ name: '', city: '', state: '', observation: '', federation_id: '' });
  const [federacoes, setFederacoes] = useState([]);
  const [enviandoPedido, setEnviandoPedido] = useState(false);

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      const { data } = await minhaEquipeApi.painel();
      setDados(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro ao carregar o painel');
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  /* PRE-SELECAO DO RELATORIO: a primeira equipe (ou a unica) ja vem escolhida.
     A escolha do usuario SEMPRE manda (so preenche quando ainda esta vazio). */
  useEffect(() => {
    if (!dados) return;
    setEquipeRelatorio((atual) => atual || dados.equipes?.[0]?.id || '');
  }, [dados]);

  /* PEDIDO DE EQUIPE: so faz sentido para o perfil `equipe` sem equipe. */
  const semEquipe = (dados?.equipes || []).length === 0;
  const carregarSolicitacao = useCallback(async () => {
    if (user?.role !== 'team') return;
    try {
      const { data } = await equipeSolicitacaoApi.minha();
      setSolicitacao(data?.pedido || null);
    } catch (e) { /* silencioso: o cartao fica no estado inicial */ }
  }, [user]);

  useEffect(() => { carregarSolicitacao(); }, [carregarSolicitacao]);

  useEffect(() => {
    if (user?.role !== 'team' || !semEquipe) return;
    federationApi.list()
      .then(({ data }) => setFederacoes(Array.isArray(data) ? data : []))
      .catch(() => setFederacoes([]));
  }, [user, semEquipe]);

  const enviarPedido = async () => {
    const nome = formEquipe.name.trim();
    if (nome.length < 3) { toast.error('Informe o nome da equipe'); return; }
    setEnviandoPedido(true);
    try {
      await equipeSolicitacaoApi.solicitar({
        name: nome,
        city: formEquipe.city.trim(),
        state: formEquipe.state.trim().toUpperCase(),
        observation: formEquipe.observation.trim(),
        federation_ids: formEquipe.federation_id ? [formEquipe.federation_id] : [],
      });
      toast.success('Pedido enviado! Aguarde a analise.');
      setFormEquipe({ name: '', city: '', state: '', observation: '', federation_id: '' });
      await carregarSolicitacao();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro ao enviar o pedido');
    } finally {
      setEnviandoPedido(false);
    }
  };

  if (carregando) {
    return <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 text-[#D4AF37] animate-spin" aria-hidden="true" /><span role="status" aria-live="polite" className="ml-2 text-sm text-[#A1A1AA]">Carregando...</span></div>;
  }
  if (!dados) return <p className="text-sm text-[#A1A1AA]">Não foi possível carregar o painel.</p>;

  const m = dados.mensalidades || {};
  const equipesDoRelatorio = dados.equipes || [];
  /* Os arquivos sao BAIXADOS POR LINK (o cookie do login autentica a chamada),
     o mesmo padrao das outras exportacoes do sistema. */
  const urlRelatorio = (formato) =>
    `/api/minha-equipe/relatorio-alunos.${formato}`
    + (equipeRelatorio ? `?team_id=${encodeURIComponent(equipeRelatorio)}` : '');

  return (
    <div className="space-y-4" data-testid="painel-equipe">
      {/* PEDIDO DE CADASTRO DE EQUIPE (perfil `equipe` sem equipe).
          O lider PEDE; a federacao ou o super_admin aprova; a equipe nasce so na
          aprovacao e os documentos sao enviados depois. Uma equipe por lider. */}
      {user?.role === 'team' && semEquipe && (
        <div className="rounded-lg border border-[#D4AF37]/30 bg-[#141414] p-4" data-testid="pedido-equipe">
          <div className="flex items-center gap-2 micro-rotulo">
            <ShieldPlus size={12} />
            {solicitacao?.status === 'pendente' ? 'Pedido de equipe' : 'Cadastre sua equipe'}
          </div>
          {solicitacao?.status === 'pendente' ? (
            <div className="mt-2 space-y-1" data-testid="pedido-aguardando">
              <p className="text-sm text-white">Pedido enviado — aguardando análise.</p>
              <p className="text-micro text-[#71717A]">Equipe: {solicitacao.name}</p>
              <p className="text-micro text-[#71717A]">
                A federação ou o administrador vai analisar. Assim que aprovado, sua equipe aparece aqui.
              </p>
            </div>
          ) : (
            <div className="mt-2 space-y-3">
              {solicitacao?.status === 'recusada' && (
                <div className="rounded border border-amber-500/30 bg-amber-500/5 p-2" data-testid="pedido-recusado">
                  <p className="text-xs text-amber-300">Pedido recusado: {solicitacao.rejected_reason || 'sem motivo informado'}</p>
                  <p className="text-micro text-[#71717A] mt-0.5">Corrija os dados e envie de novo.</p>
                </div>
              )}
              <p className="text-micro text-[#71717A]">
                Preencha os dados da sua equipe. O pedido vai para análise da federação/administrador.
              </p>
              <div className="grid gap-2 sm:grid-cols-2">
                <label className="text-micro text-[#71717A]">
                  Nome da equipe *
                  <input value={formEquipe.name} onChange={(e) => setFormEquipe({ ...formEquipe, name: e.target.value })}
                         data-testid="pedido-nome" placeholder="Ex.: A J Jiu Jitsu"
                         className="mt-1 w-full rounded border border-[#27272A] bg-[#0A0A0A] px-2 py-1.5 text-sm text-white" />
                </label>
                <label className="text-micro text-[#71717A]">
                  Cidade
                  <input value={formEquipe.city} onChange={(e) => setFormEquipe({ ...formEquipe, city: e.target.value })}
                         data-testid="pedido-cidade" placeholder="Ex.: Rio de Janeiro"
                         className="mt-1 w-full rounded border border-[#27272A] bg-[#0A0A0A] px-2 py-1.5 text-sm text-white" />
                </label>
                <label className="text-micro text-[#71717A]">
                  UF
                  <input value={formEquipe.state} onChange={(e) => setFormEquipe({ ...formEquipe, state: e.target.value })}
                         data-testid="pedido-uf" maxLength={2} placeholder="RJ"
                         className="mt-1 w-full rounded border border-[#27272A] bg-[#0A0A0A] px-2 py-1.5 text-sm text-white" />
                </label>
                <label className="text-micro text-[#71717A]">
                  Federação (opcional)
                  <select value={formEquipe.federation_id}
                          onChange={(e) => setFormEquipe({ ...formEquipe, federation_id: e.target.value })}
                          data-testid="pedido-federacao"
                          className="mt-1 w-full rounded border border-[#27272A] bg-[#0A0A0A] px-2 py-1.5 text-sm text-white">
                    <option value="">Não sei / depois</option>
                    {federacoes.map((f) => <option key={f._id} value={f._id}>{f.name}</option>)}
                  </select>
                </label>
              </div>
              <label className="text-micro text-[#71717A] block">
                Observação (opcional)
                <textarea value={formEquipe.observation} onChange={(e) => setFormEquipe({ ...formEquipe, observation: e.target.value })}
                          data-testid="pedido-observacao" rows={2} placeholder="Ex.: academia nova, 30 alunos"
                          className="mt-1 w-full rounded border border-[#27272A] bg-[#0A0A0A] px-2 py-1.5 text-sm text-white" />
              </label>
              <Button onClick={enviarPedido} disabled={enviandoPedido} data-testid="pedido-enviar"
                      className="bg-[#D4AF37] text-black hover:bg-[#E5C158] font-bold">
                {enviandoPedido ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldPlus size={16} className="mr-2" />}
                Solicitar cadastro de equipe
              </Button>
            </div>
          )}
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Cartao icone={<Users size={12} />} rotulo="Alunos na equipe" valor={dados.alunos_na_equipe}
                detalhe={`${dados.alunos_em_turmas} em turma(s) · ${dados.alunos_sem_turma} sem turma`} />
        <Cartao icone={<ClipboardList size={12} />} rotulo="Turmas ativas" valor={dados.turmas}
                detalhe={`${dados.turmas_hoje} com aula hoje`} />
        <Cartao icone={<CalendarCheck size={12} />} rotulo="Chamada de hoje"
                valor={`${dados.turmas_com_chamada_hoje}/${dados.turmas_hoje}`}
                detalhe={dados.turmas_hoje === 0 ? 'nenhuma turma hoje' : 'turmas com chamada feita'}
                cor={dados.turmas_hoje > 0 && dados.turmas_com_chamada_hoje < dados.turmas_hoje ? 'text-amber-400' : 'text-white'} />
        <Cartao icone={<DollarSign size={12} />} rotulo={`Mensalidades ${competenciaTexto(dados.competencia, true)}`}
                valor={m.total || 0}
                detalhe={`${m.pagas || 0} pagas · ${m.pendentes || 0} pendentes · ${m.atrasadas || 0} atrasadas`} />
      </div>

      <div className="rounded-lg border border-[#27272A] bg-[#141414] p-4">
        <div className="flex items-center gap-2 micro-rotulo">
          <Wallet size={12} /> Em aberto em {competenciaTexto(dados.competencia)}
        </div>
        <p className="text-2xl font-bold text-amber-400 mt-1">{formatarValor(m.valor_em_aberto || 0)}</p>
      </div>

      {/* RELATORIO DE ALUNOS (decisao do responsavel): todos os alunos da equipe,
          um embaixo do outro, com Nome, Nascimento, Sexo, Peso e Faixa — em PDF
          (A4 retrato) ou Excel. Uma equipe por vez; o Monitor gera a equipe
          inteira a que esta vinculado. */}
      <div className="rounded-lg border border-[#27272A] bg-[#141414] p-4" data-testid="painel-relatorio">
        <div className="flex items-center gap-2 micro-rotulo">
          <FileText size={12} /> Relatório de alunos
        </div>
        <p className="text-micro text-[#71717A] mt-1">
          Nome, nascimento, sexo, peso e faixa — todos os alunos da equipe
          (inclusive os pendentes de aprovação).
        </p>
        {semEquipe ? (
          <p className="text-micro text-[#71717A] mt-2">
            O relatório de alunos fica disponível quando sua equipe for aprovada.
          </p>
        ) : (
        <div className="flex flex-wrap items-end gap-2 mt-3">
          {equipesDoRelatorio.length > 1 && (
            <label className="text-micro text-[#71717A]">
              Equipe
              <select
                value={equipeRelatorio}
                onChange={(e) => setEquipeRelatorio(e.target.value)}
                data-testid="painel-relatorio-equipe"
                className="mt-1 block rounded border border-[#27272A] bg-[#0A0A0A] text-xs text-white px-2 py-1"
              >
                {equipesDoRelatorio.map((eq) => (
                  <option key={eq.id} value={eq.id}>{eq.name}</option>
                ))}
              </select>
            </label>
          )}
          <a
            href={urlRelatorio('pdf')}
            data-testid="painel-relatorio-pdf"
            className="inline-flex items-center gap-1.5 rounded border border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37] hover:text-black px-3 py-1.5 text-xs font-bold"
          >
            <FileText size={14} /> Baixar PDF
          </a>
          <a
            href={urlRelatorio('xlsx')}
            data-testid="painel-relatorio-excel"
            className="inline-flex items-center gap-1.5 rounded border border-emerald-500/40 text-emerald-400 hover:bg-emerald-500 hover:text-black px-3 py-1.5 text-xs font-bold"
          >
            <FileSpreadsheet size={14} /> Baixar Excel
          </a>
        </div>
        )}
      </div>

      {dados.alertas?.length > 0 && (
        <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-3 space-y-1" data-testid="painel-alertas">
          {dados.alertas.map((a) => (
            <p key={a} className="text-xs text-amber-300 flex items-center gap-2">
              <AlertTriangle size={12} /> {a}
            </p>
          ))}
        </div>
      )}

      <div className="rounded-lg border border-[#27272A] overflow-hidden">
        <div className="px-4 py-2 bg-[#1A1A1A] text-micro uppercase tracking-wider text-[#71717A]">
          Aulas de hoje ({dados.hoje.split('-').reverse().join('/')})
        </div>
        {(dados.turmas_do_dia || []).length === 0 ? (
          <p className="p-4 text-sm text-[#71717A]" data-testid="painel-sem-aula">
            Nenhuma turma com aula hoje.
          </p>
        ) : (
          <div className="divide-y divide-[#27272A]">
            {dados.turmas_do_dia.map((t) => (
              <div key={t.id} className="flex items-center justify-between gap-3 p-3">
                <div>
                  <p className="text-sm text-white">{t.name}</p>
                  <p className="text-micro text-[#71717A]">
                    {[t.start_time, t.end_time].filter(Boolean).join(' às ')} · {t.total_alunos} aluno(s)
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-micro ${t.chamada_feita ? 'text-emerald-400' : 'text-amber-400'}`}>
                    {t.chamada_feita ? 'chamada feita' : 'chamada pendente'}
                  </span>
                  <Button size="sm" variant="outline" onClick={() => onIrPara && onIrPara('chamada')}
                          data-testid={`painel-fazer-chamada-${t.id}`}
                          className="border-[#27272A] text-[#A1A1AA] hover:text-white">
                    Fazer chamada
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <p className="text-micro text-[#71717A]">
        Este painel é da <strong>rotina da equipe</strong>. Ele não interfere em campeonatos,
        chaves, ranking nem medalhas — e mensalidade atrasada não impede ninguém de competir.
      </p>
    </div>
  );
}
